<template>
  <div>
    <UserManagedHubModal
      :value="value"
      title="我的音色"
      add-tab-label="克隆音色"
      manage-tab-label="管理音色"
      :active-tab.sync="innerTab"
      :usage-text="usageText"
      @input="$emit('input', $event)"
      @visible-change="onHubVisibleChange"
    >
      <template #add>
        <CloneVoiceWizard
          ref="cloneWizard"
          embedded
          :voice-call="voiceCall"
          :max-my-voices="maxMyVoices"
          @saved="onCloneSaved"
          @cancel="onCloneCancel"
        />
      </template>
      <template #manage="{ switchTab }">
        <div v-if="!voiceList.length" class="vc-my-voices__empty">
          暂无音色，可切换到「克隆音色」创建
          <Button type="text" class="vc-my-voices__empty-link" @click="switchTab('add')">
            去克隆
          </Button>
        </div>
        <div v-else class="vc-my-voices__list">
          <div
            v-for="row in voiceList"
            :key="row.voice_id"
            class="vc-my-voices__item"
          >
            <div class="vc-my-voices__item-main">
              <Icon type="ios-mic-outline" size="18" class="vc-my-voices__item-icon" />
              <span class="vc-my-voices__item-name">{{ row.name || '未命名音色' }}</span>
              <Tag class="vc-my-voices__item-tag">{{ getCloneVoiceTypeLabel(row) }}</Tag>
            </div>
            <div class="vc-my-voices__item-actions">
              <VoicePlayIconButton
                :playing="playingId === row.voice_id"
                :loading="playingId === row.voice_id && audioLoading"
                @click="togglePlay(row)"
              />
              <DeleteIconButton size="md" title="删除" @click="handleDelete(row)" />
            </div>
          </div>
        </div>
      </template>
    </UserManagedHubModal>
  </div>
</template>

<script>
import CloneVoiceWizard from './CloneVoiceWizard.vue'
import UserManagedHubModal from './UserManagedHubModal.vue'
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import VoicePlayIconButton from '../shared/VoicePlayIconButton.vue'
import { getCloneVoiceTypeLabel, getStaleVoiceAuditionMessage } from '../../constants/voicePresets'
import { toPublicAudioUrl } from './voiceAudioUtils'

export default {
  name: 'MyVoicesModal',
  components: {
    CloneVoiceWizard,
    UserManagedHubModal,
    DeleteIconButton,
    VoicePlayIconButton,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: String,
      default: 'add',
    },
    voiceCall: {
      type: Object,
      required: true,
    },
    maxMyVoices: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      innerTab: 'add',
      playingId: '',
      audioLoading: false,
      audioEl: null,
    }
  },
  computed: {
    voiceList() {
      const list = this.voiceCall && Array.isArray(this.voiceCall.clonedVoices)
        ? this.voiceCall.clonedVoices
        : []
      return list.filter((row) => row && String(row.voice_id || row.id || '').trim())
    },
    usageText() {
      return `已用 ${this.voiceList.length}/${this.maxMyVoices}`
    },
  },
  beforeDestroy() {
    this.stopAudio()
  },
  methods: {
    getCloneVoiceTypeLabel,
    onHubVisibleChange(visible) {
      if (!visible) {
        this.stopAudio()
        return
      }
      this.innerTab = this.initialTab === 'manage' ? 'manage' : 'add'
      this.$nextTick(() => {
        const wizard = this.$refs.cloneWizard
        if (wizard && wizard.resetCloneWizard) {
          wizard.resetCloneWizard(false)
        }
      })
    },
    onCloneSaved() {
      this.$Message.success('已保存音色')
      this.$emit('saved')
      this.$emit('input', false)
    },
    onCloneCancel() {
      this.$emit('input', false)
    },
    getVoiceId(row) {
      return String(row && row.voice_id != null ? row.voice_id : row && row.id != null ? row.id : '').trim()
    },
    togglePlay(row) {
      const voiceId = this.getVoiceId(row)
      if (!voiceId) return

      const staleMsg = getStaleVoiceAuditionMessage(this.voiceCall)
      if (staleMsg) {
        this.$Message.warning(staleMsg)
        return
      }

      if (this.playingId === voiceId) {
        this.stopAudio()
        return
      }

      const url = toPublicAudioUrl(row.audio_file_path)
      if (!url) {
        this.$Message.warning('暂无试听音频')
        return
      }

      this.stopAudio()
      this.playingId = voiceId
      this.audioLoading = true
      const audio = new Audio(url)
      this.audioEl = audio
      audio.addEventListener('ended', () => {
        this.stopAudio()
      })
      audio.addEventListener('error', () => {
        this.audioLoading = false
        this.playingId = ''
        this.audioEl = null
        this.$Message.error('试听播放失败')
      })
      audio.play().catch(() => {
        this.audioLoading = false
        this.playingId = ''
        this.audioEl = null
        this.$Message.error('试听播放失败')
      })
      audio.addEventListener('playing', () => {
        this.audioLoading = false
      })
    },
    stopAudio() {
      if (this.audioEl) {
        this.audioEl.pause()
        this.audioEl = null
      }
      this.playingId = ''
      this.audioLoading = false
    },
    handleDelete(row) {
      const voiceId = this.getVoiceId(row)
      const name = String(row && row.name != null ? row.name : '').trim() || '该音色'
      this.$Modal.confirm({
        title: '删除音色',
        content: `确定删除「${name}」吗？删除后不可恢复。`,
        onOk: () => {
          const list = this.voiceCall && Array.isArray(this.voiceCall.clonedVoices)
            ? this.voiceCall.clonedVoices
            : []
          const index = list.findIndex((item) => this.getVoiceId(item) === voiceId)
          if (index < 0) return

          if (this.playingId === voiceId) {
            this.stopAudio()
          }

          list.splice(index, 1)

          const presetId = String(this.voiceCall.presetVoiceId || '').trim()
          if (presetId && presetId === voiceId) {
            this.$set(this.voiceCall, 'presetVoiceId', '')
          }

          const tripleVoiceId = this.voiceCall.tripleAccess
            && this.voiceCall.tripleAccess.tts
            ? String(this.voiceCall.tripleAccess.tts.voice_id || '').trim()
            : ''
          if (tripleVoiceId && tripleVoiceId === voiceId) {
            this.$set(this.voiceCall.tripleAccess.tts, 'voice_id', '')
          }

          this.$Message.success('已删除')
          this.$emit('deleted', { voice_id: voiceId })
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.vc-my-voices {
  &__empty {
    padding: 32px 16px;
    text-align: center;
    font-size: 13px;
    color: #909399;
    border: 1px dashed #e8eaec;
    border-radius: 8px;
    background: #fafafa;
    line-height: 1.6;
  }

  &__empty-link {
    padding: 0 4px !important;
    color: #5668f0 !important;
    font-size: 13px !important;
    height: auto !important;
    vertical-align: baseline;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    background: #fff;
  }

  &__item-main {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  &__item-icon {
    flex-shrink: 0;
    color: #a5a5a5;
  }

  &__item-name {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__item-tag {
    flex-shrink: 0;
    margin: 0 !important;
    border: none !important;
    background: #f4f4f5 !important;
    color: #909399 !important;
    font-size: 12px !important;
    line-height: 20px !important;
    height: 22px !important;
    padding: 0 8px !important;
  }

  &__item-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
}
</style>
